import { useState, useEffect } from "react";
import { Trophy, TrendingUp, Plus, Clock, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

const productTypes = [
  "eBook",
  "Video Course",
  "Templates",
  "Asset Pack",
  "Coaching/Consulting",
  "Software/Tool",
  "Membership",
  "Other",
];

interface ProductWin {
  id: string;
  user_id: string;
  product_title: string;
  description: string;
  amount_earned: number;
  time_period: string;
  product_type: string;
  author_name: string;
  created_at: string;
}

export default function ProductWins() {
  const { user } = useAuth();
  const [wins, setWins] = useState<ProductWin[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  const [formData, setFormData] = useState({
    product_title: "",
    description: "",
    amount_earned: "",
    time_period: "",
    product_type: "",
  });

  useEffect(() => {
    fetchWins();
  }, []);

  const fetchWins = async () => {
    const { data, error } = await supabase
      .from("product_wins")
      .select("*")
      .order("created_at", { ascending: false });
    
    if (error) {
      console.error("Error fetching wins:", error);
    } else {
      setWins(data || []);
    }
    setLoading(false);
  };

  const handleSubmit = async () => {
    if (!user) {
      toast.error("You must be logged in to share a win");
      return;
    }

    if (!formData.product_title || !formData.description || !formData.amount_earned || !formData.time_period || !formData.product_type) {
      toast.error("Please fill in all fields");
      return;
    }

    setSubmitting(true);

    const { error } = await supabase.from("product_wins").insert({
      user_id: user.id,
      product_title: formData.product_title.trim(),
      description: formData.description.trim(),
      amount_earned: parseFloat(formData.amount_earned),
      time_period: formData.time_period.trim(),
      product_type: formData.product_type,
      author_name: user.email?.split("@")[0] || "Member",
    });

    if (error) {
      console.error("Error creating win:", error);
      toast.error("Failed to share your win");
    } else {
      toast.success("Win shared successfully!");
      setFormData({
        product_title: "",
        description: "",
        amount_earned: "",
        time_period: "",
        product_type: "",
      });
      setDialogOpen(false);
      fetchWins();
    }

    setSubmitting(false);
  };

  const handleDelete = async (winId: string) => {
    const { error } = await supabase.from("product_wins").delete().eq("id", winId);
    
    if (error) {
      toast.error("Failed to delete win");
    } else {
      toast.success("Win deleted");
      fetchWins();
    }
  };

  return (
    <div className="p-6 md:p-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-yellow-400 to-amber-500 flex items-center justify-center">
            <Trophy className="w-7 h-7 text-white" />
          </div>
          <div>
            <h1 className="font-display text-2xl font-bold">Product Wins</h1>
            <p className="text-muted-foreground">Celebrating member success</p>
          </div>
        </div>

        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button variant="secondary">
              <Plus className="w-4 h-4 mr-2" />
              Share Your Win
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Share Your Product Win 🎉</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div>
                <label className="text-sm font-medium mb-1 block">Product Title</label>
                <Input
                  placeholder="e.g., YouTube Automation Course"
                  value={formData.product_title}
                  onChange={(e) => setFormData({ ...formData, product_title: e.target.value })}
                />
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block">Description</label>
                <Textarea
                  placeholder="Tell us about your win..."
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium mb-1 block">Amount Earned ($)</label>
                  <Input
                    type="number"
                    placeholder="500"
                    value={formData.amount_earned}
                    onChange={(e) => setFormData({ ...formData, amount_earned: e.target.value })}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium mb-1 block">Time Period</label>
                  <Input
                    placeholder="e.g., 2 weeks"
                    value={formData.time_period}
                    onChange={(e) => setFormData({ ...formData, time_period: e.target.value })}
                  />
                </div>
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block">Product Type</label>
                <Select
                  value={formData.product_type}
                  onValueChange={(value) => setFormData({ ...formData, product_type: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select product type" />
                  </SelectTrigger>
                  <SelectContent>
                    {productTypes.map((type) => (
                      <SelectItem key={type} value={type}>
                        {type}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button onClick={handleSubmit} disabled={submitting} className="w-full">
                {submitting ? "Sharing..." : "Share Win"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Wins Feed */}
      <div className="space-y-4 max-w-3xl">
        {loading ? (
          <div className="text-center py-12 text-muted-foreground">Loading wins...</div>
        ) : wins.length > 0 ? (
          wins.map((win) => (
            <div key={win.id} className="glass-card p-6 hover:border-primary/20 transition-colors">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-yellow-400 to-amber-500 flex items-center justify-center text-white font-bold">
                  {win.author_name.charAt(0).toUpperCase()}
                </div>
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-3">
                      <span className="font-semibold text-foreground">{win.author_name}</span>
                      <span className="text-xs px-2 py-0.5 bg-primary/20 text-primary rounded-full">
                        {win.product_type}
                      </span>
                    </div>
                    {user?.id === win.user_id && (
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-muted-foreground hover:text-destructive"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Delete this win?</AlertDialogTitle>
                            <AlertDialogDescription>
                              This will permanently delete "{win.product_title}". This action cannot be undone.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction
                              onClick={() => handleDelete(win.id)}
                              className="bg-destructive hover:bg-destructive/90"
                            >
                              Delete
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    )}
                  </div>
                  <h3 className="font-medium text-foreground mb-1">{win.product_title}</h3>
                  <p className="text-muted-foreground text-sm mb-3">{win.description}</p>
                  <div className="flex items-center gap-4 text-sm">
                    <div className="flex items-center gap-1 text-green-400">
                      <TrendingUp className="w-4 h-4" />
                      <span className="font-bold">${Number(win.amount_earned).toLocaleString()}</span>
                    </div>
                    <div className="flex items-center gap-1 text-muted-foreground">
                      <Clock className="w-4 h-4" />
                      <span>{win.time_period}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="text-center py-16">
            <Trophy className="w-16 h-16 text-muted-foreground mx-auto mb-4 opacity-40" />
            <h3 className="font-semibold text-foreground text-lg mb-2">No wins yet</h3>
            <p className="text-muted-foreground mb-6">Be the first to share your success!</p>
          </div>
        )}
      </div>
    </div>
  );
}
